"use client";

import { type UploadDirParams } from "@/utils/get-upload-dir";
import { lazy, Suspense } from "react";
import { Controller, useFormContext } from "react-hook-form";

// Define Accept type locally to avoid importing react-dropzone directly
type Accept = Record<string, string[]>;

// Lazy load the Dropzone component
const LazyDropzone = lazy(() =>
	import("../dropzone").then(module => ({
		default: module.Dropzone,
	})),
);

export interface TagOptions {
	value: string;
	label: string;
}

export type FileType = "image" | "document";

export type TagOptionsFunction = (fileType: FileType) => TagOptions[];

export interface DropzoneInputProps {
	name?: string;
	label?: string;
	description?: string;
	acceptedFiles?: Accept;
	tagOptions?: TagOptions[] | TagOptionsFunction;
	uploadDirDoc: UploadDirParams;
	fileLimit?: number;
	callback?: (res: unknown) => void;
}

export const DropzoneInput = ({
	name = "uploads",
	acceptedFiles,
	label,
	description,
	uploadDirDoc,
	tagOptions,
	fileLimit,
	callback,
	...props
}: DropzoneInputProps) => {
	const { control } = useFormContext();

	if (!acceptedFiles) {
		acceptedFiles = {
			"image/*": [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp", ".svg"],
			"application/*": [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".txt"],
			"text/*": [".txt", ".csv"],
			"application/zip": [".zip"],
			"application/x-rar-compressed": [".rar"],
		};
	}

	return (
		<Controller
			name={name}
			control={control}
			render={({ field: { value, onChange }, fieldState }) => {
				return (
					<Suspense
						fallback={
							<div className="flex items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-lg">
								<div className="text-center">
									<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-2"></div>
									<p className="text-sm text-gray-500">Loading file uploader...</p>
								</div>
							</div>
						}
					>
						<LazyDropzone
							{...props}
							name={name}
							files={value}
							acceptedFiles={acceptedFiles}
							label={label}
							description={description}
							error={fieldState?.error?.message}
							uploadDirDoc={uploadDirDoc}
							tagOptions={tagOptions}
							onChange={onChange}
							fileLimit={fileLimit}
							callback={callback}
						/>
					</Suspense>
				);
			}}
		/>
	);
};
